
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCreateCreditPackageForOrder } from "@/hooks/use-create-credit-package";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Package, 
  Plus, 
  CheckCircle, 
  AlertTriangle 
} from "lucide-react";

interface CreateCreditPackageButtonProps {
  order: any;
}

export const CreateCreditPackageButton = ({ order }: CreateCreditPackageButtonProps) => {
  const createCreditPackage = useCreateCreditPackageForOrder();

  // Check if credit packages already exist for this order
  const { data: existingPackages = [], isLoading } = useQuery({
    queryKey: ['order-credit-packages', order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('device_credit_packages')
        .select('*')
        .eq('order_id', order.id);

      if (error) throw error;
      return data || [];
    },
  });

  const handleCreatePackage = () => {
    createCreditPackage.mutate(order.id);
  };

  // Don't show button if order is not delivered
  if (order.status !== 'delivered') {
    return null;
  }

  // Don't show if packages already exist
  if (existingPackages.length > 0) {
    return (
      <div className="flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <Badge className="bg-green-100 text-green-800">
          Credit packages created ({existingPackages.length})
        </Badge>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Badge variant="outline">
        Checking packages...
      </Badge>
    );
  }

  // Show create button
  return (
    <div className="space-y-2">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          This delivered order doesn't have credit packages yet. Create them to allow credit allocation.
        </AlertDescription>
      </Alert>
      <Button
        onClick={handleCreatePackage}
        disabled={createCreditPackage.isPending}
        size="sm"
        className="w-full"
      >
        {createCreditPackage.isPending ? (
          <>
            <Package className="h-4 w-4 mr-2 animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <Plus className="h-4 w-4 mr-2" />
            Create Credit Packages
          </>
        )}
      </Button>
    </div>
  );
};
